import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const tokenStorageKey = "aurays_admin_token";

export default function CheckinScannerPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);

  const [scannerActive, setScannerActive] = useState(false);
  const [scannerResult, setScannerResult] = useState<{
    step?: "verify" | "confirm" | "already";
    error?: string;
    ticketCode?: string;
    fullName?: string;
    eventTitle?: string;
    eventTitleZh?: string;
    ticketTypeName?: string;
    ticketTypeNameZh?: string;
    phone?: string;
    email?: string;
    checkedInAt?: string;
    receiptUrl?: string | null;
    adminNotes?: string;
  } | null>(null);
  const [pendingCode, setPendingCode] = useState("");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanHandlerRef = useRef<(raw: string) => Promise<void>>(async () => {});
  const [manualCode, setManualCode] = useState("");

  const authHeaders = useMemo(() => {
    const t = token.trim();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  useEffect(() => {
    const savedToken = sessionStorage.getItem(tokenStorageKey);
    if (savedToken) {
      setToken(savedToken);
      setAuthed(true);
    }
  }, []);

  const processCheckinCode = useCallback(
    async (raw: string) => {
      setLoading(true);
      setScannerResult(null);
      try {
        // Step 1: verify ticket (no check-in yet)
        const verifyRes = await fetch("/api/admin/verify-ticket", {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ code: raw }),
        });
        const verifyData = (await verifyRes.json()) as {
          ok?: boolean;
          alreadyCheckedIn?: boolean;
          registration?: Record<string, unknown>;
          error?: string;
        };
        if (!verifyRes.ok || !verifyData?.ok) {
          setScannerResult({ error: verifyData?.error || "Ticket not found / 門票無效" });
          return;
        }

        const reg = verifyData.registration;
        if (verifyData.alreadyCheckedIn) {
          setScannerResult({
            step: "already",
            ticketCode: String(reg?.eTicketCode ?? ""),
            fullName: String(reg?.fullName ?? ""),
            eventTitle: String(reg?.eventTitle ?? ""),
            eventTitleZh: String(reg?.eventTitleZh ?? ""),
            ticketTypeName: String(reg?.ticketTypeName ?? ""),
            ticketTypeNameZh: String(reg?.ticketTypeNameZh ?? ""),
            phone: String(reg?.phone ?? ""),
            email: String(reg?.email ?? ""),
            checkedInAt: typeof reg?.checkedInAt === "string" ? reg.checkedInAt : "",
            receiptUrl: typeof reg?.receiptUrl === "string" ? reg.receiptUrl : null,
            adminNotes: typeof reg?.adminNotes === "string" ? reg.adminNotes : "",
          });
          return;
        }

        // Step 2: show details for human verify + confirm button
        setPendingCode(raw);
        setScannerResult({
          step: "verify",
          ticketCode: String(reg?.eTicketCode ?? ""),
          fullName: String(reg?.fullName ?? ""),
          eventTitle: String(reg?.eventTitle ?? ""),
          eventTitleZh: String(reg?.eventTitleZh ?? ""),
          ticketTypeName: String(reg?.ticketTypeName ?? ""),
          ticketTypeNameZh: String(reg?.ticketTypeNameZh ?? ""),
          phone: String(reg?.phone ?? ""),
          email: String(reg?.email ?? ""),
          receiptUrl: typeof reg?.receiptUrl === "string" ? reg.receiptUrl : null,
          adminNotes: typeof reg?.adminNotes === "string" ? reg.adminNotes : "",
        });
      } catch {
        setScannerResult({ error: "Network error. Please try again. / 網絡錯誤，請重試。" });
      } finally {
        setLoading(false);
      }
    },
    [authHeaders],
  );

  const confirmCheckin = useCallback(async () => {
    if (!pendingCode) return;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/check-in-by-code", {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ code: pendingCode }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        alreadyCheckedIn?: boolean;
        registration?: Record<string, unknown>;
        error?: string;
      };
      if (!res.ok || !data?.ok) {
        if (data?.alreadyCheckedIn) {
          // Race: someone else checked in between verify and confirm
          setScannerResult((prev) => ({
            ...prev,
            step: "already",
            checkedInAt: new Date().toISOString(),
          }));
          return;
        }
        setScannerResult({ error: data?.error || "Check-in failed / 入場失敗" });
        return;
      }
      const reg = data.registration;
      setScannerResult((prev) => ({
        ...prev,
        step: "confirm",
        checkedInAt: String(reg?.checkedInAt ?? new Date().toISOString()),
        ticketCode: String(reg?.eTicketCode ?? prev?.ticketCode ?? ""),
        adminNotes: (typeof reg?.adminNotes === "string" ? reg.adminNotes : prev?.adminNotes) || "",
      }));
    } catch {
      setScannerResult({ error: "Network error. Please try again. / 網絡錯誤，請重試。" });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, pendingCode]);

  scanHandlerRef.current = processCheckinCode;

  const startScanner = useCallback(async () => {
    setScannerResult(null);
    setScannerActive(true);
    let el = document.getElementById("qr-scanner-reader");
    let attempts = 0;
    while (!el && attempts < 20) {
      await new Promise((r) => setTimeout(r, 50));
      el = document.getElementById("qr-scanner-reader");
      attempts++;
    }
    if (!el) {
      setScannerResult({ error: "Scanner UI failed to load. Please refresh. / 掃描器載入失敗，請重新整理。" });
      setScannerActive(false);
      return;
    }

    try {
      const scanner = new Html5Qrcode("qr-scanner-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await scanner.stop();
          scannerRef.current = null;
          setScannerActive(false);
          scanHandlerRef.current(decodedText);
        },
        () => {
          /* scan failure — silently continue */
        },
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err ?? "");
      setScannerResult({
        error: msg.includes("NotAllowed")
          ? "Camera permission denied. Please allow camera access in browser settings. / 鏡頭權限被拒，請在瀏覽器設定中允許鏡頭存取。"
          : msg.includes("NotFound")
            ? "No camera found. Please connect a camera or use manual code entry. / 找不到鏡頭，請連接鏡頭或使用手動輸入。"
            : `Camera error: ${msg || "unknown"} / 鏡頭錯誤`,
      });
      setScannerActive(false);
    }
  }, []);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        /* intentionally empty */
      }
      scannerRef.current = null;
    }
    setScannerActive(false);
  }, []);

  const manualCheckin = useCallback(async () => {
    if (!manualCode.trim()) return;
    await processCheckinCode(manualCode.trim());
    setManualCode("");
  }, [manualCode, processCheckinCode]);

  const renderHighlightedRemarks = (text: string) => {
    const parts = [];
    const regex = /(free)/gi;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={lastIndex}>{text.slice(lastIndex, match.index)}</span>);
      }
      parts.push(
        <span key={match.index} className="bg-yellow-200 text-yellow-900 font-semibold px-1 rounded">
          {match[0]}
        </span>,
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(<span key={lastIndex}>{text.slice(lastIndex)}</span>);
    }
    return parts.length ? parts : text;
  };

  const resetScanner = useCallback(() => {
    setScannerResult(null);
    setPendingCode("");
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-xl mx-auto px-6 md:px-10">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Check-in Scanner / 入場掃碼</h1>
                <p className="text-sm text-gray-500">
                  Scan attendee QR codes or enter e-ticket codes manually to check in guests.
                </p>
              </div>
            </div>

            {!authed ? (
              <div className="max-w-md">
                <p className="text-sm text-gray-500">
                  Please sign in via{" "}
                  <a href="/aurays-admin" className="text-[#7C55CC] hover:underline">
                    Aurays Admin
                  </a>{" "}
                  first.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Manual code entry */}
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") manualCheckin();
                    }}
                    placeholder="Enter e-ticket code manually / 手動輸入電子門票編號"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900"
                    disabled={loading}
                  />
                  <button
                    onClick={manualCheckin}
                    disabled={loading || !manualCode.trim()}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-colors ${
                      loading || !manualCode.trim() ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"
                    }`}
                  >
                    {loading ? "..." : "Check In / 入場"}
                  </button>
                </div>

                {/* Scanner */}
                <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6 text-center">
                  <div
                    id="qr-scanner-reader"
                    className={`max-w-sm mx-auto rounded-xl overflow-hidden ${scannerActive ? "" : "hidden"}`}
                  />
                  {!scannerActive && !scannerResult ? (
                    <button
                      onClick={startScanner}
                      className="px-6 py-3 rounded-full text-sm font-semibold text-white bg-[#5B3FA0] hover:bg-[#4A2F8A] transition-colors"
                    >
                      Start Scanner / 開啟掃描器
                    </button>
                  ) : scannerActive ? (
                    <button
                      onClick={stopScanner}
                      className="mt-4 px-5 py-2.5 rounded-full text-sm font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                    >
                      Stop Scanner / 停止掃描
                    </button>
                  ) : null}

                  {/* Scanner result */}
                  {scannerResult ? (
                    <div
                      className={`mt-4 rounded-2xl p-5 text-left ${
                        scannerResult.error
                          ? "bg-red-50 border border-red-100"
                          : scannerResult.step === "already"
                            ? "bg-amber-50 border border-amber-100"
                            : scannerResult.step === "confirm"
                              ? "bg-emerald-50 border border-emerald-100"
                              : "bg-white border border-gray-200"
                      }`}
                    >
                      {scannerResult.error ? (
                        <div className="space-y-2">
                          <p className="font-semibold text-red-700">Failed / 失敗</p>
                          <p className="text-sm text-red-600">{scannerResult.error}</p>
                          <button
                            onClick={resetScanner}
                            className="px-4 py-2 rounded-full text-xs font-semibold bg-white text-red-700 hover:bg-red-50 transition-colors"
                          >
                            Try Again / 重試
                          </button>
                        </div>
                      ) : scannerResult.step === "already" ? (
                        <div className="space-y-3">
                          <p className="font-semibold text-amber-700 text-lg">Already Checked In / 已入場</p>
                          <div className="bg-white/60 rounded-xl p-3 space-y-1.5 text-sm">
                            <p>
                              <span className="text-gray-400">Name:</span>{" "}
                              <span className="font-semibold text-gray-900">{scannerResult.fullName || "-"}</span>
                            </p>
                            <p>
                              <span className="text-gray-400">Event:</span>{" "}
                              <span className="font-semibold text-gray-900">
                                {scannerResult.eventTitleZh || scannerResult.eventTitle || "-"}
                              </span>
                            </p>
                            <p>
                              <span className="text-gray-400">Ticket:</span>{" "}
                              <span className="font-semibold text-gray-900">
                                {scannerResult.ticketTypeNameZh || scannerResult.ticketTypeName || "-"}
                              </span>
                            </p>
                            <p>
                              <span className="text-gray-400">Code:</span>{" "}
                              <span className="font-mono text-xs text-gray-600">{scannerResult.ticketCode}</span>
                            </p>
                            {scannerResult.adminNotes && (
                              <p>
                                <span className="text-gray-400">Remarks:</span>{" "}
                                <span className="text-gray-800">
                                  {renderHighlightedRemarks(scannerResult.adminNotes)}
                                </span>
                              </p>
                            )}
                            {scannerResult.checkedInAt ? (
                              <p>
                                <span className="text-gray-400">Checked in at:</span>{" "}
                                <span className="text-gray-700">
                                  {new Date(scannerResult.checkedInAt).toLocaleString()}
                                </span>
                              </p>
                            ) : null}
                          </div>
                          <button
                            onClick={resetScanner}
                            className="px-4 py-2 rounded-full text-xs font-semibold bg-white text-amber-700 hover:bg-amber-50 transition-colors"
                          >
                            Scan Next / 繼續掃描
                          </button>
                        </div>
                      ) : scannerResult.step === "confirm" ? (
                        <div className="space-y-3">
                          <p className="font-semibold text-emerald-700 text-lg">Checked In! / 入場成功!</p>
                          <div className="bg-white/60 rounded-xl p-3 space-y-1.5 text-sm">
                            <p>
                              <span className="text-gray-400">Name:</span>{" "}
                              <span className="font-semibold text-gray-900">{scannerResult.fullName || "-"}</span>
                            </p>
                            <p>
                              <span className="text-gray-400">Event:</span>{" "}
                              <span className="font-semibold text-gray-900">
                                {scannerResult.eventTitleZh || scannerResult.eventTitle || "-"}
                              </span>
                            </p>
                            <p>
                              <span className="text-gray-400">Code:</span>{" "}
                              <span className="font-mono text-xs text-gray-600">{scannerResult.ticketCode}</span>
                            </p>
                            {scannerResult.adminNotes && (
                              <p>
                                <span className="text-gray-400">Remarks:</span>{" "}
                                <span className="text-gray-800">
                                  {renderHighlightedRemarks(scannerResult.adminNotes)}
                                </span>
                              </p>
                            )}
                            {scannerResult.checkedInAt ? (
                              <p>
                                <span className="text-gray-400">Checked in at:</span>{" "}
                                <span className="text-gray-700">
                                  {new Date(scannerResult.checkedInAt).toLocaleString()}
                                </span>
                              </p>
                            ) : null}
                          </div>
                          <button
                            onClick={resetScanner}
                            className="px-4 py-2 rounded-full text-xs font-semibold bg-white text-emerald-700 hover:bg-emerald-50 transition-colors"
                          >
                            Scan Next / 繼續掃描
                          </button>
                        </div>
                      ) : (
                        /* step === "verify" — show details + confirm button */
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 text-lg">Verify Attendee / 確認身份</p>
                            {scannerResult.receiptUrl ? (
                              <a
                                href={scannerResult.receiptUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-auto inline-flex rounded-full bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 hover:bg-gray-200 transition-colors"
                              >
                                View Receipt / 查看收據
                              </a>
                            ) : null}
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-gray-400 text-xs">Name / 姓名</span>
                                <p className="font-semibold text-gray-900">{scannerResult.fullName || "-"}</p>
                              </div>
                              <div>
                                <span className="text-gray-400 text-xs">Phone / 電話</span>
                                <p className="font-semibold text-gray-900">{scannerResult.phone || "-"}</p>
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-400 text-xs">Email</span>
                              <p className="text-gray-800">{scannerResult.email || "-"}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-gray-400 text-xs">Event / 活動</span>
                                <p className="font-semibold text-gray-900">
                                  {scannerResult.eventTitleZh || scannerResult.eventTitle || "-"}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-400 text-xs">Ticket / 門票</span>
                                <p className="font-semibold text-gray-900">
                                  {scannerResult.ticketTypeNameZh || scannerResult.ticketTypeName || "-"}
                                </p>
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-400 text-xs">E-Ticket Code</span>
                              <p className="font-mono text-xs text-gray-600">{scannerResult.ticketCode}</p>
                            </div>
                            {scannerResult.adminNotes && (
                              <div>
                                <span className="text-gray-400 text-xs">Remarks / 備註</span>
                                <p className="text-gray-800">{renderHighlightedRemarks(scannerResult.adminNotes)}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={confirmCheckin}
                              disabled={loading}
                              className={`flex-1 px-5 py-3 rounded-full text-sm font-semibold text-white transition-colors ${
                                loading ? "bg-gray-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                            >
                              {loading ? "Processing..." : "Confirm Check-in / 確認入場"}
                            </button>
                            <button
                              onClick={resetScanner}
                              disabled={loading}
                              className="px-4 py-3 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              Cancel / 取消
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
